import { FormlyFieldConfig } from '@ngx-formly/core';
import { toDateOnly } from '@tpa/shared';
import {
  AddressHint,
  ChildHint,
  EmployeeHints,
  HintItem,
  HintKind,
  PersonHint,
} from './hint.model';

/** Cheap deep clone for the plain-JSON hint payloads (no Dates / class instances). */
function clonePlain<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

/**
 * Reads the card-owner id from a formly `formState`. Returns `null` for the self-service default
 * (`0`/missing) so the hints service falls back to the current user.
 */
export function resolveOwnerId(formState: Record<string, unknown> | undefined | null): number | null {
  const value = formState?.['ownerId'];
  return typeof value === 'number' && value > 0 ? value : null;
}

const trimmed = (value?: string | null): string => (value ?? '').trim();
const hasValue = (value?: string | null): boolean => trimmed(value).length > 0;

/** Maps an {@link AddressHint} onto the `sharedAddress()` model shape used across every form. */
export function addressHintToModel(hint: AddressHint): Record<string, string | undefined> {
  return {
    ulice: hint.ulice ?? undefined,
    popCislo: hint.popCislo ?? undefined,
    orCislo: hint.orCislo ?? undefined,
    mesto: hint.mesto ?? undefined,
    psc: hint.psc ?? undefined,
    zeme: hint.zeme ?? undefined,
  };
}

/**
 * Normalizes a hint's birth date to the shape the `date-picker` type accepts.
 *
 * Hints reach us with two different date shapes: history read from an annual tax settlement keeps
 * whatever the form stored (a full ISO instant), while history read from an employee card is
 * date-only `YYYY-MM-DD` (the backend's `CustomDateOnlyConverter`). Only the latter is widened,
 * through the shared {@link toDateOnly}, into the canonical offset-less `YYYY-MM-DDT00:00:00` —
 * `libs/shared/src/lib/utils/date-only.utils.ts` documents why both the missing offset and the
 * time part matter (a bare `YYYY-MM-DD` leaves a Syncfusion picker looking broken, a `Z` instant
 * shifts the calendar day by one on its way through the backend).
 *
 * Anything that isn't date-only is passed through untouched — an instant deliberately included, so
 * a settlement hint keeps exactly what the form stored. A calendar-invalid day (e.g. `2023-02-30`)
 * is not a parseable date either, so it passes through as well rather than rolling over into March.
 */
function normalizeBirthDate(value?: string | null): string | undefined {
  const raw = trimmed(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? toDateOnly(raw) : raw || undefined;
}

/**
 * Builds the `birth-number-and-birth-date` sub-group model. When a Czech birth number is present
 * we mark it as such (so the birth date is derived and disabled, matching manual entry); otherwise
 * only the birth date is kept.
 */
export function birthGroupFromHint(hint: {
  rodneCislo?: string;
  datumNarozeni?: string;
}): { ceskeRodneCislo: boolean; rodneCislo?: string; datumNarozeni?: string } {
  const ceske = hasValue(hint.rodneCislo);
  return {
    ceskeRodneCislo: ceske,
    rodneCislo: hint.rodneCislo ?? undefined,
    datumNarozeni: normalizeBirthDate(hint.datumNarozeni),
  };
}

/** Builds one children (`deti`) row model from a {@link ChildHint}. */
export function childHintToRow(hint: ChildHint): Record<string, unknown> {
  return {
    jmeno: hint.jmeno ?? undefined,
    prijmeni: hint.prijmeni ?? undefined,
    rodneCisloADatumNarozeni: birthGroupFromHint(hint),
    uplatneni: [{}],
  };
}

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------

function formatAddressLines(a: AddressHint): { title: string; subtitle?: string } {
  const streetLine = [
    trimmed(a.ulice),
    [trimmed(a.popCislo), trimmed(a.orCislo)].filter(hasValue).join('/'),
  ]
    .filter(hasValue)
    .join(' ')
    .trim();
  const cityLine = [
    [trimmed(a.psc), trimmed(a.mesto)].filter(hasValue).join(' '),
    trimmed(a.zeme),
  ]
    .filter(hasValue)
    .join(', ')
    .trim();
  return {
    title: streetLine || cityLine,
    subtitle: streetLine ? cityLine || undefined : undefined,
  };
}

function personName(p: { jmeno?: string; prijmeni?: string }): string {
  return [trimmed(p.jmeno), trimmed(p.prijmeni)].filter(hasValue).join(' ').trim();
}

function birthLabel(p: { rodneCislo?: string; datumNarozeni?: string }): string {
  return trimmed(p.rodneCislo) || trimmed(p.datumNarozeni);
}

/** Turns the hint array for a given kind into display-ready {@link HintItem}s. */
export function hintsToItems(kind: HintKind, hints: EmployeeHints | null | undefined): HintItem[] {
  if (!hints) {
    return [];
  }
  switch (kind) {
    case 'address':
      return (hints.addresses ?? []).map((raw) => ({ raw, ...formatAddressLines(raw) }));
    case 'child':
      return (hints.children ?? []).map((raw) => ({
        raw,
        title: personName(raw) || birthLabel(raw),
        subtitle: personName(raw) ? birthLabel(raw) || undefined : undefined,
      }));
    case 'person':
      // Persons carry only identity now — their address (when any) lives in the shared address pool.
      return (hints.otherPersons ?? []).map((raw) => ({
        raw,
        title: personName(raw) || birthLabel(raw),
        subtitle: personName(raw) ? birthLabel(raw) || undefined : undefined,
      }));
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Formly helpers
// ---------------------------------------------------------------------------

/** The dotted key of a field split into a path (mirrors ngx-formly's `getKeyPath`). */
function fieldKeyPath(field: FormlyFieldConfig): (string | number)[] {
  const key = field.key;
  if (key == null) {
    return [];
  }
  if (Array.isArray(key)) {
    return key as (string | number)[];
  }
  return typeof key === 'string' ? key.split('.') : [key];
}

/**
 * Sets `value` at a field's position in the root model, creating intermediate objects/arrays as
 * needed. Mirrors ngx-formly's internal `assignFieldValue`, which is what `FieldArrayType.add()`
 * uses to seed a missing array before splicing into it.
 */
function assignFieldModelValue(field: FormlyFieldConfig, value: unknown): void {
  let paths = fieldKeyPath(field);
  if (paths.length === 0) {
    return;
  }
  let root = field;
  while (root.parent) {
    root = root.parent;
    paths = [...fieldKeyPath(root), ...paths];
  }
  let cursor = root.model as Record<string | number, unknown>;
  for (let i = 0; i < paths.length - 1; i++) {
    const path = paths[i];
    if (!cursor[path] || typeof cursor[path] !== 'object') {
      cursor[path] = /^\d+$/.test(String(paths[i + 1])) ? [] : {};
    }
    cursor = cursor[path] as Record<string | number, unknown>;
  }
  cursor[paths[paths.length - 1]] = value;
}

/**
 * Fills values into a formly group field's model and rebuilds it, so controls that are currently
 * unregistered behind a closed `hide` gate are hydrated from the model when formly re-registers
 * them (ngx-formly unregisters hidden controls even with `resetFieldOnHide: false`, so a plain
 * `formControl.patchValue` would silently drop those keys). Use this — not `patchValue` — whenever
 * the target fields may be gated behind a yes/no toggle (e.g. `dalsiPoplatnik`).
 */
export function patchFieldModel(
  field: FormlyFieldConfig | undefined,
  values: Record<string, unknown>
): boolean {
  const build = field?.options?.build;
  if (!field || !build) {
    return false;
  }
  let model = field.model as Record<string, unknown> | undefined;
  if (!model || typeof model !== 'object') {
    // Seed the model. A keyless (transparent) field reads its parent's model, so seed through the
    // nearest keyed ancestor — that is the object this field's getter resolves to.
    let seedField: FormlyFieldConfig | undefined = field;
    while (seedField && seedField.key == null) {
      seedField = seedField.parent;
    }
    if (!seedField) {
      return false;
    }
    if (!(seedField.model && typeof seedField.model === 'object')) {
      assignFieldModelValue(seedField, {});
    }
    model = field.model as Record<string, unknown> | undefined;
    if (!model || typeof model !== 'object') {
      return false;
    }
  }
  Object.assign(model, clonePlain(values));
  field.formControl?.markAsDirty();
  build(field);
  return true;
}

/** Depth-first search for a descendant field config with the given `key`. */
export function findFieldByKey(
  fields: FormlyFieldConfig[] | undefined,
  key: string
): FormlyFieldConfig | undefined {
  if (!fields) {
    return undefined;
  }
  for (const field of fields) {
    if (field.key === key) {
      return field;
    }
    const nested =
      findFieldByKey(field.fieldGroup, key) ??
      findFieldByKey(field.fieldArray ? [field.fieldArray as FormlyFieldConfig] : undefined, key);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

/**
 * Appends an item to a `type: 'repeat'` (ngx-formly {@link import('@ngx-formly/core').FieldArrayType})
 * field from outside the array component. Mirrors `FieldArrayType.add()`: it mutates the field's
 * model array in place and rebuilds the field via the shared `options.build`, which reconciles the
 * `fieldGroup` and registers the new row's controls.
 *
 * @returns `true` when the item was appended, `false` when the field can't accept it.
 */
export function appendFormlyArrayItem(
  arrayField: FormlyFieldConfig | undefined,
  item: unknown
): boolean {
  const build = arrayField?.options?.build;
  if (!arrayField || !build) {
    return false;
  }

  // `field.model` is a getter deriving from the root model, so it can't be assigned. When the array
  // doesn't exist yet we seed it on the root model (like `FieldArrayType.add()` does) and read it
  // back through the getter; otherwise we mutate the live array in place.
  let model = arrayField.model as unknown[] | undefined;
  if (!Array.isArray(model)) {
    assignFieldModelValue(arrayField, []);
    model = arrayField.model as unknown[] | undefined;
    if (!Array.isArray(model)) {
      return false;
    }
  }

  model.push(clonePlain(item));
  arrayField.formControl?.markAsDirty();
  arrayField.formControl?.markAsTouched();
  build(arrayField);
  return true;
}

/**
 * Fills the first row of a `type: 'repeat'` field for which `isEmpty` is true (e.g. the blank,
 * non-removable row that `allowRemoveFirst: false` auto-seeds), otherwise appends a new row. Avoids
 * leaving an empty leftover row when the repeat pre-seeds one.
 */
export function fillOrAppendFormlyArrayItem(
  arrayField: FormlyFieldConfig | undefined,
  item: Record<string, unknown>,
  isEmpty: (row: unknown) => boolean
): boolean {
  const model = arrayField?.model as unknown[] | undefined;
  if (Array.isArray(model) && arrayField?.fieldGroup) {
    const index = model.findIndex((row) => isEmpty(row));
    if (index >= 0 && arrayField.fieldGroup[index]) {
      return patchFieldModel(arrayField.fieldGroup[index], item);
    }
  }
  return appendFormlyArrayItem(arrayField, item);
}

/** True when a children (`deti`) row carries no identity yet — i.e. an auto-seeded/blank row. */
export function isChildRowEmpty(row: unknown): boolean {
  if (!row || typeof row !== 'object') {
    return true;
  }
  const r = row as { jmeno?: string; prijmeni?: string; rodneCisloADatumNarozeni?: { rodneCislo?: string; datumNarozeni?: string } };
  const birth = r.rodneCisloADatumNarozeni ?? {};
  return !hasValue(r.jmeno) && !hasValue(r.prijmeni) && !hasValue(birth.rodneCislo) && !hasValue(birth.datumNarozeni);
}
